export enum BillStatus {
  Open = 'OPEN',
  Closed = 'CLOSED',
  Snoozed = 'SNOOZED',
  Overdue = 'OVERDUE',
  Paid = 'PAID',
  WaitingPayment = 'WAITING_PAYMENT',
}

export interface BillSnoozeDetailsSnooze {
  financing_id: string
  total_value: number
  deferred_remaining_value: number
  interest_percentage: number
  interest_value: number
  future_debt_value: number
  minimum_payment: number
  bill_due_date: string
  effectivation_date: string
}

export enum BillSnoozeActionStatus {
  Preview = 'PREVIEW',
  Review = 'REVIEW',
  Disabled = 'DISABLED',
}

export interface BillSnoozeDetails {
  has_snooze: boolean // deprecated
  snooze: BillSnoozeDetailsSnooze
  action_status: BillSnoozeActionStatus
}

export interface BillFlags {
  is_empty: boolean
  is_zero: boolean
  has_debit: boolean
  show_amounts_header: boolean
}

export enum BillLineItemV2Kind {
  Installment = 'INSTALLMENT',
  InstallmentNumber = 'INSTALLMENT_NUMBER',
  CashinPayment = 'CASHIN_PAYMENT',
  CashinReceivable = 'CASHIN_RECEIVABLE',
  CashinFinancing = 'CASHIN_FINANCING',
  CashinCredit = 'CASHIN_CREDIT',
  AggregateCashInReceivable = 'AGGREGATE_CASHIN_RECEIVABLE',
  Balance = 'BALANCE',
  DebtPurchase = 'DEBT_PURCHASE',
  DebtFine = 'DEBT_FINE',
  DebtFineAggregate = 'DEBT_FINE_AGGREGATE',
  DebtChargeback = 'DEBT_CHARGEBACK',
  DebtChargebackAggregate = 'DEBT_CHARGEBACK_AGGREGATE',
  DebtFinancing = 'DEBT_FINANCING',
  Reservation = 'RESERVATION',
}

export interface BillLineInstallmentNumberV2 {
  id?: string
  kind: BillLineItemV2Kind.InstallmentNumber
  metadata: {
    current_installment: number
    total_number_of_installments: number
  }
}
export interface BillLineCommonV2 {
  id: string
  amount_cents: number
  reference_date: string
  payer_id: string
}

// INSTALLMENT
export enum BillLineInstallmentStatus {
  Open = 'OPEN',
  Canceled = 'CANCELED',
  Reimbursed = 'REIMBURSED',
}
export interface BillLineInstallmentVersions {
  invoice_total_amount_cents: number
  invoice_num_installments: number
  installment_number: number
  installment_total_amount_cents: number
  installment_settlement_date: string
  installment_estimated_due_date: string
  installment_edited_at: string
  installment_internal_status: BillLineInstallmentStatus
}
export interface BillLineInstallmentV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.Installment
  metadata: {
    invoice_id: string
    installment_id: string
    invoice_supplier_name: string
    invoice_created_at: string
    invoice_approved_at: string
    tax_invoice_number: string | null
    tax_invoice_access_key: string
    versions: BillLineInstallmentVersions[]
    edition?: {
      edited: boolean
      canceled: boolean
      reimbursed: boolean
      lastEditionDate: string
      newAmount: number
      originalAmount: number
    }
  }
}

export interface BillLineProcessingV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.Reservation
  metadata: {
    supplier_name: string
    reservation_created_at: string
  }
}

export interface BillLineAggregateCashInKindV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.AggregateCashInReceivable
  metadata: {
    cashin_ids: string[]
  }
}

export interface BillLineCashinV2 extends BillLineCommonV2 {
  kind:
    | BillLineItemV2Kind.CashinPayment
    | BillLineItemV2Kind.CashinReceivable
    | BillLineItemV2Kind.CashinFinancing
    | BillLineItemV2Kind.CashinCredit
  metadata: {
    cashin_id: string
    amount_cents: number
    created_at: string
  }
}

export interface BillLineBalanceV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.Balance
  metadata: {
    amount_cents: number
    balance_at: string
    cashins: any[]
  }
}

export interface BillLineDebitPurchaseV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.DebtPurchase
  metadata: {
    purchase_debt_id: string
    base_debt_id: string
    total_amount_cents: number
    paid_amount_cents: number
    created_at: string
    activated_at: string
    due_at: string
  }
}
export interface ChargebackDebtMetadata {
  chargeback_debt_id: string
  base_debt_id: string
  total_amount_cents: number
  paid_amount_cents: number
  created_at: string
  activated_at: string
  due_at: string
}

export interface BillLineDebitChargebackV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.DebtChargeback
  metadata: ChargebackDebtMetadata
}

export interface BillLineDebitChargebackAggregateV4 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.DebtChargebackAggregate
  metadata: {
    chargeback_debts: ChargebackDebtMetadata[]
  }
}

export interface BillLineDebitFinancingV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.DebtFinancing
  metadata: {
    financing_debt_id: string
    financing_id: string
    base_debt_id: string
    total_amount_cents: number
    paid_amount_cents: number
    created_at: string
    activated_at: string
    due_at: string
  }
}

export type BillLineDebitFineAggregateV2Metadata = {
  fine_debt_id: string
  base_debt_id: string
  total_amount_cents: number
  paid_amount_cents: number
  created_at: string
  activated_at: string
  due_at: string
}

export type BillLineDebitFineV2Metadata = {
  fine_debt_id: string
  fine_kind: string
  fine_rate: number
  base_debt_id: string
  total_amount_cents: number
  paid_amount_cents: number
  created_at: string
  activated_at: string
  due_at: string
  purchase_debt_amount: number
  purchase_debt_due_at: string
}

export interface BillLineDebitFineAggregateV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.DebtFineAggregate
  metadata: {
    debts: BillLineDebitFineAggregateV2Metadata[]
  }
}

export interface BillLineDebitFineV2 extends BillLineCommonV2 {
  kind: BillLineItemV2Kind.DebtFine
  metadata: BillLineDebitFineV2Metadata
}

export type BillLineDebitV2 =
  | BillLineDebitPurchaseV2
  | BillLineDebitChargebackV2
  | BillLineDebitChargebackAggregateV4
  | BillLineDebitFinancingV2
  | BillLineDebitFineAggregateV2
  | BillLineDebitFineV2

export type LineItemsV2 =
  | BillLineInstallmentV2
  | BillLineInstallmentNumberV2
  | BillLineCashinV2
  | BillLineBalanceV2
  | BillLineAggregateCashInKindV2
  | BillLineDebitV2
  | BillLineProcessingV2

export interface BillRaw {
  closing_date: string
  deliquency_fine_rate: number
  due_amount: number
  due_date: string
  effective_due_date: string
  flags: BillFlags
  id: string
  minimum_amount: number
  paid_amount: number
  payable: boolean
  snooze_details?: BillSnoozeDetails
  spent_amount: number
  status: BillStatus
}

export interface BillLIneItemGroup {
  line_items: LineItemsV2[]
  is_paid: boolean
}

export interface BillRawV3 extends BillRaw {
  line_item_groups: BillLIneItemGroup[]
}

export interface Bill extends BillRawV3 {
  balance: number
}

export interface BillListResponseV3 {
  current_bill_id: string
  bills: BillRawV3[]
}

export interface BillList {
  current_bill_id: string
  bills: Bill[]
}

export interface BillListPast {
  bills: Bill[]
}

export enum PaymentKind {
  PIX = 'PIX',
  BOLETO = 'BOLETO',
}

export interface PayResponse {
  line_code: string
  link: string
  amount_cents: number
  due_date: string
  has_boleto: boolean
  kind?: PaymentKind
}

export interface SnoozeSimulationResponse {
  future_debt_value: number
  interest_percentage: number
  interest_value: number
  minimum_payment: number
  financing_id: string
}

export interface AddReferralSeller {
  contact: string
  seller_name: string
}
